import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api, apiError } from "../api/client";
import {
  IconOrders, IconLoader, IconAlert, IconSearch,
} from "../components/Icons";
import SearchableSelect from "../components/SearchableSelect";
import { useCustomPaymentMethods } from "../hooks/useCustomPaymentMethods";

interface OrderListItem {
  id: string;
  number: string | null;
  status: string;
  order_type: string;
  total_minor: number;
  total_net_minor: number;
  currency: string;
  opened_at: string;
  updated_at: string;
  closed_at: string | null;
  branch_name: string;
  employee_name: string | null;
  item_count: number;
  note: string | null;
  payment_methods: string[];
}
interface OrderItem {
  id: string;
  name: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  vat_rate: number;
  note: string | null;
}
interface Payment {
  id: string;
  method: string;
  provider?: string | null;
  amount: number;
  tip: number;
  currency: string;
  paid_at: string;
}
interface OrderDetail extends OrderListItem {
  closed_by_name: string | null;
  eet_status: string | null;
  eet_pok: string | null;
  subtotal: number;
  discount_total: number;
  rounding: number;
  vat_breakdown: Record<string, { base: number; vat: number; gross: number }> | null;
  note: string | null;
  customer_note: string | null;
  items: OrderItem[];
  payments: Payment[];
}
interface Branch { id: string; name: string; }
interface OrderListPage { items: OrderListItem[]; total: number; }

type SortKey = "updated" | "number" | "branch" | "employee" | "total" | "note";

const STATUS_BADGES: Record<string, string> = {
  open: "badge badge-yellow", paying: "badge badge-yellow",
  paid: "badge badge-green", cancelled: "badge badge-red", refunded: "badge badge-red",
};
const PAYMENT_KEYS = [
  "cash", "card", "sumup", "voucher", "meal_voucher",
  "writeoff", "loyalty_points", "combined", "other",
];

function formatMoney(minor: number, currency: string): string {
  return new Intl.NumberFormat("cs-CZ", { style: "currency", currency, minimumFractionDigits: 0 }).format(minor / 100);
}

function normalizePaymentKey(key: string): string {
  return key.startsWith("PaymentMethod.") ? key.slice("PaymentMethod.".length) : key;
}

function paymentDisplayKey(method: string, provider?: string | null): string {
  if (method === "card" && provider === "sumup") return "sumup";
  if (provider === "meal_voucher") return "meal_voucher";
  if (method === "other" && provider) return provider;
  return normalizePaymentKey(method);
}

export default function Orders() {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("paid");
  const [filterBranch, setFilterBranch] = useState("all");
  const [filterPayment, setFilterPayment] = useState("all");
  const [pageSize, setPageSize] = useState(50);
  const [pageIndex, setPageIndex] = useState(0);
  const [total, setTotal] = useState(0);
  const [sortKey, setSortKey] = useState<SortKey>("updated");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [selectedOrder, setSelectedOrder] = useState<OrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [isVatPayer, setIsVatPayer] = useState(false);
  const [vatRates, setVatRates] = useState<{ code: string; rate: number }[]>([]);

  useEffect(() => {
    api.get<{ is_vat_payer: boolean }>("/tenant/profile")
      .then(r => setIsVatPayer(r.data.is_vat_payer)).catch(() => {});
    api.get<{ code: string; rate: number }[]>("/catalog/vat-rates")
      .then(r => setVatRates(r.data)).catch(() => {});
  }, []);

  const vatCode = (rate: number) =>
    vatRates.find(v => Math.abs(v.rate - rate) < 0.01)?.code ?? `${rate}%`;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams({
        limit: String(pageSize),
        offset: String(pageIndex * pageSize),
        sort: sortKey,
        order: sortDir,
      });
      if (filterBranch !== "all") params.set("branch_id", filterBranch);
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (filterPayment !== "all") params.set("payment_method", filterPayment);
      if (debouncedSearch) params.set("search", debouncedSearch);
      const [ords, brs] = await Promise.all([
        api.get<OrderListPage>(`/orders?${params}`).then(r => r.data),
        api.get<Branch[]>("/branches?limit=500").then(r => r.data).catch(() => []),
      ]);
      setOrders(ords.items);
      setTotal(ords.total);
      setBranches(brs);
    } catch (e) {
      setErr(apiError(e, t("orders.loadError")));
    } finally {
      setLoading(false);
    }
  }, [filterBranch, filterStatus, filterPayment, debouncedSearch, pageSize, pageIndex, sortKey, sortDir, t]);

  useEffect(() => { load(); }, [load]);

  // Reset to first page whenever filters/search/sort change.
  useEffect(() => { setPageIndex(0); }, [filterBranch, filterStatus, filterPayment, debouncedSearch, pageSize, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  const statusLabel = (s: string) => t(`orders.statusLabels.${s}`, { defaultValue: s });
  const typeLabel = (s: string) => t(`orders.types.${s}`, { defaultValue: s });
  const customMethods = useCustomPaymentMethods();
  const payLabel = (key: string) => {
    const k = normalizePaymentKey(key);
    const custom = customMethods.find((m) => m.code === k);
    return custom ? custom.name : t(`orders.pay.${k}`, { defaultValue: k });
  };
  const paymentMethodLabel = (methods: string[] | undefined) =>
    !methods || methods.length === 0 ? "—" : methods.map(payLabel).join(" + ");
  const paymentMethodName = (p: Payment) => payLabel(paymentDisplayKey(p.method, p.provider));

  const COLUMNS: { label: string; sort: SortKey | null }[] = [
    { label: t("orders.colNumber"), sort: "number" },
    { label: t("orders.colBranch"), sort: "branch" },
    { label: t("orders.colEmployee"), sort: "employee" },
    ...(isVatPayer ? [{ label: t("orders.colTotalNet"), sort: null }] : []),
    { label: isVatPayer ? t("orders.colTotalGross") : t("orders.colTotal"), sort: "total" as SortKey },
    { label: t("orders.colPayment"), sort: null },
    { label: t("orders.colUpdated"), sort: "updated" },
    { label: t("orders.colStatus"), sort: null },
    { label: t("orders.colNote"), sort: "note" },
  ];

  async function showDetail(id: string) {
    setDetailLoading(true);
    setSelectedOrder(null);
    try {
      const { data } = await api.get<OrderDetail>(`/orders/${id}`);
      setSelectedOrder(data);
    } catch (e) {
      setErr(apiError(e, t("orders.detailLoadError")));
    } finally {
      setDetailLoading(false);
    }
  }

  if (loading && orders.length === 0) {
    return <div style={{ display: "flex", justifyContent: "center", padding: 80 }}><IconLoader size={32} className="muted" /></div>;
  }

  // ---- Detail modal content ----
  const detailModal = (selectedOrder || detailLoading) && (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={() => setSelectedOrder(null)}>
      <div className="card" style={{ width: "100%", maxWidth: 800, maxHeight: "90vh", overflow: "auto", padding: 24 }} onClick={(e) => e.stopPropagation()}>
        {detailLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><IconLoader size={32} className="muted" /></div>
        ) : selectedOrder && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
              <div>
                <h2 style={{ fontSize: 20, fontWeight: 600 }}>{t("orders.receipt")} {selectedOrder.number || "—"}</h2>
                <p style={{ color: "var(--muted)", fontSize: 13, marginTop: 4 }}>{selectedOrder.branch_name} · {typeLabel(selectedOrder.order_type)}</p>
              </div>
              <button className="ghost" onClick={() => setSelectedOrder(null)}>{t("orders.close")}</button>
            </div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 20 }}>
            <div className="card" style={{ padding: 24, background: "var(--bg-elevated)" }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>{t("orders.items")}</h3>
              <table>
                <thead><tr><th>{t("orders.colItem")}</th><th>{t("orders.colQty")}</th><th>{t("orders.colPrice")}</th>{isVatPayer && <th>DPH</th>}<th>{t("orders.colSum")}</th></tr></thead>
                <tbody>
                  {selectedOrder.items.map(it => (
                    <tr key={it.id}>
                      <td style={{ fontWeight: 600 }}>{it.name}{it.note && <div style={{ fontSize: 12, color: "var(--muted)" }}>{it.note}</div>}</td>
                      <td>{it.quantity}</td>
                      <td>{formatMoney(it.unit_price, selectedOrder.currency)}</td>
                      {isVatPayer && <td>{vatCode(it.vat_rate)}</td>}
                      <td style={{ fontWeight: 600 }}>{formatMoney(it.line_total, selectedOrder.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 6, fontSize: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}><span color="var(--muted)">{t("orders.subtotal")}</span><span>{formatMoney(selectedOrder.subtotal, selectedOrder.currency)}</span></div>
                {selectedOrder.discount_total > 0 && <div style={{ display: "flex", justifyContent: "space-between", color: "var(--red)" }}><span>{t("orders.discount")}</span><span>-{formatMoney(selectedOrder.discount_total, selectedOrder.currency)}</span></div>}
                {selectedOrder.rounding !== 0 && <div style={{ display: "flex", justifyContent: "space-between" }}><span>{t("orders.rounding")}</span><span>{formatMoney(selectedOrder.rounding, selectedOrder.currency)}</span></div>}
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 18, fontWeight: 700, borderTop: "1px solid var(--border)", paddingTop: 8, marginTop: 4 }}>
                  <span>{t("orders.total")}</span><span>{formatMoney(selectedOrder.total_minor, selectedOrder.currency)}</span>
                </div>
                {isVatPayer && selectedOrder.vat_breakdown && Object.keys(selectedOrder.vat_breakdown).length > 0 && (
                  <table style={{ marginTop: 12 }}>
                    <thead><tr><th>{t("orders.vatRateCol")}</th><th>{t("orders.vatBaseCol")}</th><th>DPH</th><th>{t("orders.vatGrossCol")}</th></tr></thead>
                    <tbody>
                      {Object.entries(selectedOrder.vat_breakdown)
                        .sort((a, b) => Number(b[0]) - Number(a[0]))
                        .map(([rate, b]) => (
                          <tr key={rate}>
                            <td style={{ fontWeight: 600 }}>{vatCode(Number(rate))} · {rate}%</td>
                            <td>{formatMoney(b.base, selectedOrder.currency)}</td>
                            <td>{formatMoney(b.vat, selectedOrder.currency)}</td>
                            <td style={{ fontWeight: 600 }}>{formatMoney(b.gross, selectedOrder.currency)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div className="card" style={{ padding: 20, background: "var(--bg-elevated)" }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{t("orders.info")}</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
                  <Row label={t("orders.colNumber")} value={selectedOrder.number || "—"} />
                  <Row label={t("orders.colStatus")} value={<span className={STATUS_BADGES[selectedOrder.status]}>{statusLabel(selectedOrder.status)}</span>} />
                  <Row label={t("orders.type")} value={typeLabel(selectedOrder.order_type)} />
                  <Row label={t("orders.colBranch")} value={selectedOrder.branch_name} />
                  <Row label={t("orders.colEmployee")} value={selectedOrder.employee_name || "—"} />
                  <Row label={t("orders.closedBy")} value={selectedOrder.closed_by_name || "—"} />
                  <Row label={t("orders.opened")} value={new Date(selectedOrder.opened_at).toLocaleString("cs-CZ")} />
                  {selectedOrder.closed_at && <Row label={t("orders.paidAt")} value={new Date(selectedOrder.closed_at).toLocaleString("cs-CZ")} />}
                  {selectedOrder.note && <Row label={t("orders.colNote")} value={selectedOrder.note} />}
                  {selectedOrder.customer_note && <Row label={t("orders.buyer")} value={<span style={{ whiteSpace: "pre-line" }}>{selectedOrder.customer_note}</span>} />}
                  {selectedOrder.eet_status && (
                    <Row label="EET" value={t(`orders.eet.${selectedOrder.eet_status}`, { defaultValue: selectedOrder.eet_status })} />
                  )}
                  {selectedOrder.eet_pok && <Row label="POK" value={<span className="mono" style={{ fontSize: 11 }}>{selectedOrder.eet_pok}</span>} />}
                </div>
              </div>
              <div className="card" style={{ padding: 20, background: "var(--bg-elevated)" }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{t("orders.payments")}</h3>
                {selectedOrder.payments.length === 0 ? (
                  <div style={{ color: "var(--muted)", fontSize: 13 }}>{t("orders.noPayments")}</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
                    {selectedOrder.payments.map(p => (
                      <div key={p.id}>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span>{paymentMethodName(p)}</span>
                          <span style={{ fontWeight: 600 }}>{formatMoney(p.amount, p.currency)}</span>
                        </div>
                        {p.tip > 0 && (
                          <div style={{ display: "flex", justifyContent: "space-between", color: "var(--muted)", fontSize: 12 }}>
                            <span>{t("orders.tip")}</span>
                            <span>+{formatMoney(p.tip, p.currency)}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          </>
        )}
      </div>
    </div>
  );

  // ---- List view ----
  return (
    <div>
      <div className="page-header">
        <h1>{t("orders.title")}</h1>
        <p>{t("orders.subtitle")}</p>
      </div>

      {err && (
        <div className="card" style={{ padding: "12px 16px", marginBottom: 20, background: "var(--red-dim)", color: "var(--red)", display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
          <IconAlert size={16} /> {err}
        </div>
      )}

      <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "flex-start" }}>
        <div style={{ position: "relative", flex: "1 1 auto", minWidth: 0, maxWidth: 360 }}>
          <IconSearch size={18} className="muted" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
          <input placeholder={t("orders.searchPlaceholder")} value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 38, width: "100%" }} />
        </div>
        <div style={{ width: 180, flexShrink: 0 }}>
          <SearchableSelect
            value={filterStatus}
            onChange={setFilterStatus}
            options={[
              { value: "all", label: t("orders.allStatuses") },
              ...["paid", "open", "paying", "cancelled", "refunded"].map(s => ({ value: s, label: statusLabel(s) })),
            ]}
            placeholder={t("orders.allStatuses")}
            searchPlaceholder={t("orders.searchStatus")}
          />
        </div>
        <div style={{ width: 200, flexShrink: 0 }}>
          <SearchableSelect
            value={filterBranch}
            onChange={setFilterBranch}
            options={[{ value: "all", label: t("dashboard.allBranches") }, ...branches.map(b => ({ value: b.id, label: b.name }))]}
            placeholder={t("dashboard.allBranches")}
            searchPlaceholder={t("dashboard.searchBranch")}
          />
        </div>
        <div style={{ width: 180, flexShrink: 0 }}>
          <SearchableSelect
            value={filterPayment}
            onChange={setFilterPayment}
            options={[
              { value: "all", label: t("orders.allMethods") },
              ...PAYMENT_KEYS.map(key => ({ value: key, label: payLabel(key) })),
            ]}
            placeholder={t("orders.allMethods")}
            searchPlaceholder={t("orders.searchMethod")}
          />
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: "center" }}>
          <IconOrders size={48} className="muted" />
          <h3 style={{ marginTop: 16, fontSize: 18, fontWeight: 600 }}>{t("orders.empty")}</h3>
          <p style={{ color: "var(--muted)", fontSize: 14, marginTop: 8 }}>{t("orders.emptyHint")}</p>
        </div>
      ) : (
        <div className="card">
          <table>
            <thead>
              <tr>
                {COLUMNS.map(c => (
                  <th
                    key={c.label}
                    onClick={c.sort ? () => toggleSort(c.sort!) : undefined}
                    style={c.sort ? { cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" } : undefined}
                  >
                    {c.label}{c.sort && sortKey === c.sort ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id} style={{ cursor: "pointer" }} onClick={() => showDetail(o.id)}>
                  <td className="mono" style={{ fontWeight: 600 }}>{o.number || "—"}</td>
                  <td>{o.branch_name}</td>
                  <td>{o.employee_name || "—"}</td>
                  {isVatPayer && <td>{formatMoney(o.total_net_minor, o.currency)}</td>}
                  <td style={{ fontWeight: 600 }}>{formatMoney(o.total_minor, o.currency)}</td>
                  <td style={{ fontSize: 13 }}>{paymentMethodLabel(o.payment_methods)}</td>
                  <td style={{ color: "var(--muted)", fontSize: 13 }}>{new Date(o.closed_at ?? o.updated_at).toLocaleString("cs-CZ", { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                  <td><span className={STATUS_BADGES[o.status] || "badge"}>{statusLabel(o.status)}</span></td>
                  <td style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "12px 16px", borderTop: "1px solid var(--border)", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--muted)" }}>
              <span>{t("orders.perPage")}</span>
              <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} style={{ width: "auto" }}>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>· {t("orders.totalCount", { total })}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <button className="ghost" disabled={pageIndex === 0} onClick={() => setPageIndex(i => i - 1)}>‹ {t("orders.prev")}</button>
              <span style={{ color: "var(--muted)" }}>{pageIndex + 1} / {pageCount}</span>
              <button className="ghost" disabled={pageIndex >= pageCount - 1} onClick={() => setPageIndex(i => i + 1)}>{t("orders.next")} ›</button>
            </div>
          </div>
        </div>
      )}

      {detailModal}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <span style={{ color: "var(--muted)" }}>{label}</span>
      <span style={{ textAlign: "right" }}>{value}</span>
    </div>
  );
}
